import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Alert, Container, Group, Text } from '@mantine/core'
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react'
import { API_BASE } from '../api/client'

const CHECK_INTERVAL_MS = 4_000
const CHECK_TIMEOUT_MS = 1_500
const FAILURE_WINDOW_MS = 30_000
const FAILURE_THRESHOLD = 3
const RECOVERY_WINDOW_MS = 20_000
const RECOVERY_THRESHOLD = 5
const INCIDENT_TITLE = '\u0412\u0435\u0434\u0443\u0442\u0441\u044f \u0442\u0435\u0445\u043d\u0438\u0447\u0435\u0441\u043a\u0438\u0435 \u0440\u0430\u0431\u043e\u0442\u044b'
const INCIDENT_MESSAGE = '\u0427\u0430\u0441\u0442\u044c \u0444\u0443\u043d\u043a\u0446\u0438\u0439 \u043c\u043e\u0436\u0435\u0442 \u0431\u044b\u0442\u044c \u0432\u0440\u0435\u043c\u0435\u043d\u043d\u043e \u043d\u0435\u0434\u043e\u0441\u0442\u0443\u043f\u043d\u0430. \u041c\u044b \u0430\u0432\u0442\u043e\u043c\u0430\u0442\u0438\u0447\u0435\u0441\u043a\u0438 \u043f\u0440\u043e\u0432\u0435\u0440\u044f\u0435\u043c \u0441\u043e\u0435\u0434\u0438\u043d\u0435\u043d\u0438\u0435 \u0438 \u0443\u0431\u0435\u0440\u0435\u043c \u044d\u0442\u043e \u0441\u043e\u043e\u0431\u0449\u0435\u043d\u0438\u0435 \u043f\u043e\u0441\u043b\u0435 \u0432\u043e\u0441\u0441\u0442\u0430\u043d\u043e\u0432\u043b\u0435\u043d\u0438\u044f.'
const INCIDENT_CHECKING = '\u041f\u0440\u043e\u0432\u0435\u0440\u044f\u0435\u043c'

export default function ServiceIncidentBanner() {
  const [visible, setVisible] = useState(false)
  const failuresRef = useRef<number[]>([])
  const successesRef = useRef<number[]>([])
  const location = useLocation()
  const isLandingPage = location.pathname === '/'

  useEffect(() => {
    let mounted = true
    let intervalId: ReturnType<typeof setInterval> | null = null
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    let controller: AbortController | null = null

    const prune = (items: number[], windowMs: number, now: number) => {
      while (items.length > 0 && now - items[0] > windowMs) items.shift()
      return items
    }

    const recordSuccess = () => {
      const now = Date.now()
      failuresRef.current = []
      successesRef.current = prune([...successesRef.current, now], RECOVERY_WINDOW_MS, now)

      if (successesRef.current.length >= RECOVERY_THRESHOLD) {
        setVisible(false)
      }
    }

    const recordFailure = () => {
      const now = Date.now()
      successesRef.current = []
      failuresRef.current = prune([...failuresRef.current, now], FAILURE_WINDOW_MS, now)

      if (failuresRef.current.length >= FAILURE_THRESHOLD) {
        setVisible(true)
      }
    }

    const checkHealth = async () => {
      controller?.abort()
      if (timeoutId) clearTimeout(timeoutId)

      controller = new AbortController()
      timeoutId = setTimeout(() => controller?.abort(), CHECK_TIMEOUT_MS)

      try {
        const res = await fetch(`${API_BASE}/health`, {
          method: 'GET',
          cache: 'no-store',
          signal: controller.signal
        })
        if (!mounted) return
        if (res.ok) recordSuccess()
        else recordFailure()
      } catch {
        if (!mounted) return
        recordFailure()
      } finally {
        if (timeoutId) {
          clearTimeout(timeoutId)
          timeoutId = null
        }
      }
    }

    const onOffline = () => recordFailure()
    const onOnline = () => void checkHealth()

    window.addEventListener('offline', onOffline)
    window.addEventListener('online', onOnline)
    void checkHealth()
    intervalId = setInterval(checkHealth, CHECK_INTERVAL_MS)

    return () => {
      mounted = false
      window.removeEventListener('offline', onOffline)
      window.removeEventListener('online', onOnline)
      if (intervalId) clearInterval(intervalId)
      if (timeoutId) clearTimeout(timeoutId)
      controller?.abort()
    }
  }, [])

  if (!visible || isLandingPage) return null

  return (
    <div className="service-incident-banner" role="status" aria-live="polite">
      <Container size={1750} px={{ base: 16, sm: 28, lg: 36 }}>
        <Alert
          className="service-incident-banner__alert"
          icon={<IconAlertTriangle size={18} />}
          color="orange"
          radius="lg"
          variant="light"
        >
          <Group justify="space-between" gap="sm" wrap="nowrap">
            <div>
              <Text size="sm" fw={700}>{INCIDENT_TITLE}</Text>
              <Text size="sm" c="dimmed">{INCIDENT_MESSAGE}</Text>
            </div>
            <Group gap={6} wrap="nowrap" className="service-incident-banner__checking">
              <IconRefresh size={15} />
              <Text size="xs" fw={600}>{INCIDENT_CHECKING}</Text>
            </Group>
          </Group>
        </Alert>
      </Container>
    </div>
  )
}
